/**
 * Theme colours for the water, read from CSS custom properties.
 *
 * The tokens are hex (`--ae-water`, `--ae-crest`, defined in `palette.ts`), the
 * same notation as every other colour in the app, and the shader wants 0..1
 * channels. They used to be stored as six separate decimal properties so the
 * engine could `parseFloat` them — a second notation that existed only for the
 * engine's convenience and could not be checked against the palette.
 *
 * `rgb()` is accepted too: when a token is set to `var(--ae-over)` (the flood
 * colour, D-8), the computed value is whatever the browser serialises.
 */

export type Rgb = readonly [number, number, number];

const CHANNEL_MAX = 255;
const HEX6 = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i;
const HEX3 = /^#([0-9a-f])([0-9a-f])([0-9a-f])$/i;
const RGB = /^rgba?\(\s*(\d{1,3})[\s,]+(\d{1,3})[\s,]+(\d{1,3})\s*(?:[,/][^)]*)?\)$/i;

/** 0..1 channels, or null for anything that is not a colour this reads. */
export function parseCssColor(text: string): Rgb | null {
  const value = text.trim();
  const channels =
    HEX6.exec(value)?.slice(1).map((h) => Number.parseInt(h, 16)) ??
    HEX3.exec(value)?.slice(1).map((h) => Number.parseInt(h + h, 16)) ??
    RGB.exec(value)?.slice(1).map((d) => Number.parseInt(d, 10));
  if (channels === undefined) return null;
  const [r, g, b] = channels;
  if (r === undefined || g === undefined || b === undefined) return null;
  if (r > CHANNEL_MAX || g > CHANNEL_MAX || b > CHANNEL_MAX) return null;
  return [r / CHANNEL_MAX, g / CHANNEL_MAX, b / CHANNEL_MAX];
}
